import { ProjectConstructionType } from '@consigli/types';
import { Building, Check, Landmark, type LucideIcon, Plus, Signpost } from 'lucide-react';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RichCheckboxMark } from '@/components/rich-checkbox-mark';

import './project-type-field.css';

type ProjectTypeGroupId = 'buildings' | 'other' | 'transportation';

/**
 * Which group each construction type sits in.
 *
 * A total `Record`, so a member added to `ProjectConstructionType` will not
 * compile until somebody says where it belongs. The alternative is a new type
 * that quietly appears in no group and cannot be picked at all.
 *
 * BRIDGE is under Buildings + Places because 11971:210827 draws it there, not
 * because it is a building. HOTEL is the one member no frame draws; it is a
 * building, it is selectable on this form today, and leaving it out would take
 * that away.
 */
const GROUP_OF: Record<ProjectConstructionType, ProjectTypeGroupId> = {
  [ProjectConstructionType.OFFICE_BUILDING]: 'buildings',
  [ProjectConstructionType.RESIDENTIAL_BUILDING]: 'buildings',
  [ProjectConstructionType.HOSPITAL]: 'buildings',
  [ProjectConstructionType.DATA_CENTER]: 'buildings',
  [ProjectConstructionType.OTHER_BUILDING]: 'buildings',
  [ProjectConstructionType.BRIDGE]: 'buildings',
  [ProjectConstructionType.HOTEL]: 'buildings',
  [ProjectConstructionType.ROAD]: 'transportation',
  [ProjectConstructionType.OTHER_INFRASTRUCTURE]: 'other',
};

interface ProjectTypeGroup {
  /** The sentence under the title. The group that lists chips has none. */
  descriptionKey?: string;
  icon: LucideIcon;
  id: ProjectTypeGroupId;
  titleKey: string;
}

/**
 * The groups, in the order 11971:210827 stacks them.
 *
 * Its Water ("Water, Dams, Pipelines") and Environment ("Windmills, solar
 * panels") cards are absent: `ProjectConstructionType` has no member either
 * one could store, so drawing them would offer a choice the API cannot record.
 * They arrive here when the enum does.
 */
const GROUPS: readonly ProjectTypeGroup[] = [
  { icon: Building, id: 'buildings', titleKey: 'project-creation.project-type-buildings' },
  {
    descriptionKey: 'project-creation.project-type-transportation-help',
    icon: Signpost,
    id: 'transportation',
    titleKey: 'project-creation.project-type-transportation',
  },
  {
    descriptionKey: 'project-creation.project-type-other-help',
    icon: Landmark,
    id: 'other',
    titleKey: 'project-creation.project-type-other',
  },
];

/**
 * A group's types, in enum order — which is the order 11971:210827 lays its
 * chips out in, so the source of truth for both is the enum.
 */
const typesIn = (id: ProjectTypeGroupId): ProjectConstructionType[] =>
  Object.values(ProjectConstructionType).filter((type) => GROUP_OF[type] === id);

/**
 * The card, as an inset ring rather than a `border`.
 *
 * Figma strokes a frame inside its box, so the frame's card is 16 + content +
 * 16 with the stroke painted within; a CSS border is added outside the padding
 * box and would make the same card 2px taller. The ring is the faithful
 * translation and it keeps the padding on a token — the call
 * pages/project-overview/construction-type-field.tsx records for the same card.
 *
 * No `gap`: the chips row is always in the DOM so it can animate, and a flex
 * gap would hold 12px open above a row collapsed to nothing. The spacing sits
 * inside the animated box instead, where it is clipped with the rest of it.
 *
 * `select-none` on the card rather than on each label: every word in here —
 * the group's title, its description, the prompt and the chips — is the text
 * of a control, and dragging across a control should not leave a blue smear
 * behind. `user-select` reaches the descendants, so one class covers the card
 * and everything in it.
 */
const CARD =
  'flex w-full select-none flex-col rounded-[var(--ds-radius-10)] bg-[var(--ds-color-bg-primary)] p-4 transition-shadow duration-[120ms] ease-[ease] motion-reduce:transition-none';

const RING_DEFAULT =
  'shadow-[inset_0_0_0_1px_var(--ds-color-border-primary)] hover:shadow-[inset_0_0_0_1px_var(--ds-color-border-secondary)]';

/**
 * The group is ticked and holds nothing, which is 11971:212035. Not an error
 * and not reported as one — it is the prompt going from grey to amber to say
 * the card is waiting on a chip. It clears the moment one is picked.
 *
 * `--ds-color-text-warning` for a ring because no `border-warning` token
 * ships; both are #D97706, which is the value the frame draws.
 */
const RING_WARNING = 'shadow-[inset_0_0_0_1px_var(--ds-color-text-warning)]';

/**
 * The 32px brand-light tile, 8 of padding around a 16px mark.
 *
 * The mark's colour and stroke 1.5 are the values components/project-folder-tile.tsx
 * derives from its own export: the same tile, the same icon set, the same
 * flattened export.
 */
const ICON_TILE =
  'flex shrink-0 items-center justify-center rounded-lg bg-[var(--ds-color-bg-brand-light)] p-2';

const CHIP =
  'flex cursor-pointer items-center gap-2 rounded-[40px] px-3 py-1.5 text-[13px] leading-[18px] transition-colors duration-[120ms] ease-[ease] motion-reduce:transition-none';

/**
 * Clear, and clear-on-hover: the frame's `bg-secondary-dark` and bg-tertiary
 * its hover, the same pair, in the same order, that invite-recipients-field.tsx
 * draws its chips with.
 */
const CHIP_OFF =
  'bg-[var(--ds-color-bg-secondary-dark)] text-[var(--ds-color-raw-stone-600)] hover:bg-[var(--ds-color-bg-tertiary)]';

/** Picked: brand-light behind the frame's brand dark, and a tick for the plus. */
const CHIP_ON = 'bg-[var(--ds-color-bg-brand-light)] text-[var(--ds-color-text-brand-dark)]';

/** The cross-fade between a chip's plus and its tick. */
const MARK_SWAP =
  'transition-[opacity,transform] duration-[120ms] ease-[ease] motion-reduce:transition-none';

interface ProjectTypeChipProps {
  checked: boolean;
  label: string;
  onCheckedChange: (checked: boolean) => void;
}

/**
 * One type inside a group. Figma calls it `chip-dismissable` (11971:211859)
 * and gives it three states: clear with a plus, hover, and picked with a tick
 * on brand-light.
 *
 * A real `<input type="checkbox">` under a `<label>`, so the whole pill is the
 * pointer target, the row is reachable by keyboard for free, and the state is
 * the input's rather than something ARIA has to be told. The marks take their
 * colour from the label through `currentColor`, which is what makes the tick
 * brand-dark and the plus grey without either being written twice.
 */
const ProjectTypeChip: FC<ProjectTypeChipProps> = ({ checked, label, onCheckedChange }) => (
  <label className={`${CHIP} ${checked ? CHIP_ON : CHIP_OFF}`}>
    <input
      checked={checked}
      className="sr-only"
      onChange={(event) => onCheckedChange(event.target.checked)}
      type="checkbox"
    />
    {/* Both marks stay mounted and cross-fade. Swapping one for the other in
        a single frame pops, and a plus rotating into a tick says nothing — a
        plus is symmetric at 90 degrees. Scale carries the change instead. */}
    <span aria-hidden="true" className="relative flex size-4 shrink-0 items-center justify-center">
      <Plus
        className={`absolute size-4 ${MARK_SWAP} ${checked ? 'scale-75 opacity-0' : 'scale-100 opacity-100'}`}
        strokeWidth={1.5}
      />
      <Check
        className={`absolute size-4 ${MARK_SWAP} ${checked ? 'scale-100 opacity-100' : 'scale-75 opacity-0'}`}
        strokeWidth={1.5}
      />
    </span>
    {label}
  </label>
);

interface ProjectTypeFieldProps {
  onChange: (constructionTypes: ProjectConstructionType[]) => void;
  value: ProjectConstructionType[];
}

/**
 * The create form's Project type field: Figma 11971:210827, built from
 * 11971:211801 (a group with no chips) and 11971:211901 (a group with them).
 *
 * ## The shape of it
 *
 * A stack of group cards over one flat list of `ProjectConstructionType`. The
 * groups are presentation: nothing stores them, the API takes the same array
 * it always did, and `GROUP_OF` is the only place the arrangement lives.
 *
 * A group with more than one type discloses chips when it is ticked; a group
 * with exactly one *is* that type, and its card toggles it. That is the rule
 * behind the frame drawing chips under Buildings + Places and none under the
 * others, rather than a flag per group — split Transportation into road and
 * rail tomorrow and it gets chips without an edit here.
 *
 * ## Ticking a group is not picking a type
 *
 * A multi-type group can be open and empty, and the frame says what that looks
 * like: 11971:212035 turns the card's ring and its prompt amber. So the tick
 * is a disclosure, the chips are the value, and the amber is the card asking
 * for one. Closing the group clears whatever it held — leaving types selected
 * under a collapsed card would put them in the request with nothing on screen
 * saying so.
 *
 * A single-type group has no such gap: its tick is the type.
 */
export const ProjectTypeField: FC<ProjectTypeFieldProps> = ({ onChange, value }) => {
  const { t } = useTranslation();
  const selected = new Set(value);

  /**
   * Multi-type groups the user has opened but not yet filled. A group holding
   * a type is open by virtue of holding it, so this only has to remember the
   * empty ones — which is also what makes an existing project's types open
   * their own groups with no effect to do it.
   */
  const [opened, setOpened] = useState<ProjectTypeGroupId[]>([]);

  // Emitted in enum order rather than click order, so the value does not
  // depend on how the user got there.
  const emit = (next: Set<ProjectConstructionType>) =>
    onChange(Object.values(ProjectConstructionType).filter((type) => next.has(type)));

  const toggleType = (type: ProjectConstructionType, checked: boolean) => {
    const next = new Set(selected);

    if (checked) next.add(type);
    else next.delete(type);

    emit(next);
  };

  return (
    <div className="flex flex-col gap-2">
      {GROUPS.map((group) => {
        const types = typesIn(group.id);
        const held = types.filter((type) => selected.has(type));
        const hasChips = types.length > 1;
        const isOpen = held.length > 0 || opened.includes(group.id);
        const wantsAType = hasChips && isOpen && held.length === 0;
        const Icon = group.icon;
        const titleId = `project-type-${group.id}-title`;
        const helpId = `project-type-${group.id}-help`;

        const toggleGroup = (checked: boolean) => {
          if (!hasChips) {
            toggleType(types[0], checked);
            return;
          }

          setOpened((current) =>
            checked ? [...current, group.id] : current.filter((openId) => openId !== group.id),
          );

          // Closing a group takes its types with it.
          if (!checked && held.length > 0) {
            const next = new Set(selected);
            held.forEach((type) => next.delete(type));
            emit(next);
          }
        };

        return (
          <div className={`${CARD} ${wantsAType ? RING_WARNING : RING_DEFAULT}`} key={group.id}>
            {/* The header is the label, and the chips sit outside it: a chip is
                a control of its own, and nesting it inside this label would
                make every chip click toggle the group as well. */}
            <label
              className={`flex w-full cursor-pointer gap-3 ${hasChips ? 'items-center' : 'items-start'}`}
            >
              {/* Named by the title alone, described by the line under it.
                  Both sit inside the label, so without `aria-labelledby` the
                  name would be the two run together — "Transportation roads,
                  rails and transport" — and the description would be part of
                  what the control is called rather than something said about
                  it. */}
              <input
                aria-describedby={group.descriptionKey === undefined ? undefined : helpId}
                aria-labelledby={titleId}
                checked={isOpen}
                className="sr-only"
                onChange={(event) => toggleGroup(event.target.checked)}
                type="checkbox"
              />

              <span className={ICON_TILE}>
                <Icon
                  aria-hidden="true"
                  className="size-4 text-[var(--ds-color-text-brand-dark)]"
                  strokeWidth={1.5}
                />
              </span>

              <span className="flex min-w-0 flex-1 flex-col">
                <span
                  className="text-[14px] leading-5 text-[var(--ds-color-text-primary)]"
                  id={titleId}
                >
                  {t(group.titleKey)}
                </span>
                {group.descriptionKey !== undefined && (
                  <span
                    className="text-[13px] leading-5 text-[var(--ds-color-raw-stone-600)]"
                    id={helpId}
                  >
                    {t(group.descriptionKey)}
                  </span>
                )}
              </span>

              {/* pt-[2.5px] is the frame's optical nudge on the cards whose
                  header is top-aligned; the chip group centres instead. */}
              <span className={`flex shrink-0 items-center ${hasChips ? '' : 'pt-[2.5px]'}`}>
                <RichCheckboxMark checked={isOpen} />
              </span>
            </label>

            {/* Mounted whether or not the group is open, because a row that
                unmounts cannot animate out. project-type-field.css collapses
                the track to 0fr and takes it out of the tab order with
                `visibility`, so a closed group is closed to the keyboard and
                to a screen reader as well as to the eye. */}
            {hasChips && (
              <div className="project-type-chips" data-open={isOpen}>
                {/* The clipped box carries no padding of its own. A grid
                    item at height 0 still keeps its padding under
                    `border-box`, so a `pt-3` here would hold 12px open on a
                    collapsed group — the very gap moving this inside was
                    meant to remove. The spacing goes one level further in,
                    where it is clipped with the rest. */}
                <div>
                  <div className="flex flex-col gap-3 pt-3">
                    <p
                      className={`text-[13px] leading-5 transition-colors duration-[120ms] ease-[ease] motion-reduce:transition-none ${
                        wantsAType
                          ? 'text-[var(--ds-color-text-warning)]'
                          : 'text-[var(--ds-color-raw-stone-600)]'
                      }`}
                    >
                      {t('project-creation.project-type-select')}
                    </p>

                    {/* Named, so the chips are announced as belonging to this
                      group rather than as nine loose checkboxes after it. */}
                    <div
                      aria-label={t(group.titleKey)}
                      className="flex flex-wrap content-start items-start gap-2"
                      role="group"
                    >
                      {types.map((type) => (
                        <ProjectTypeChip
                          checked={selected.has(type)}
                          key={type}
                          label={t(`project-construction-type.${type}`)}
                          onCheckedChange={(checked) => toggleType(type, checked)}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
